"use client";

import React, { useState } from "react";
import {
  AOSAnimatedContainer,
  BackgroundRevealButton,
  ExternalLinkWrapper,
  FontResizerWrapper,
} from "../..";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { GeneralEnquirySchema } from "@/schemas/pagesSchema/enquiries/generalEnquirySchema";
import { submitBusinessEnquiryForm } from "@/actions/frontEndActions/action";
import { useErrorToast, useSuccessToast } from "@/lib/hooks";
import PhoneNumberInput from "../../common/PhoneNumberInput";

const PackageEnqiryForm = ({
  currentLanguage = "en",
  slug = "",
  extraClass = "",
}) => {
  const [open, setOpen] = useState(false);
  const [isThankYouShown, setIsThankYouShown] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(GeneralEnquirySchema),
    defaultValues: {
      name: "",
      email: "",
      country_code: "",
      phoneNumber: "",
      message: "",
    },
  });
  const translate = useTranslations();

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    const response = await submitBusinessEnquiryForm(
      data,
      currentLanguage,
      slug
    );

    if (response.success) {
      useSuccessToast(response?.message || "Content created successfully.");
      reset();
      setIsThankYouShown(true);
    } else {
      console.log("Email Response: ", response); // TODO Remove --
      useErrorToast(response?.message || "Something went wrong.");
    }
  };

  return (
    <>
      {isThankYouShown ? (
        <div
          className={`w-full max-w-[900px] mx-auto min-h-[40vh] ${extraClass}`}
        >
          <div className="relative w-full h-full flex flex-col justify-center items-center gap-5">
            <h2 className="subtitle-1 md:heading-2 ltr:font-family-primary rtl:font-family-arabic text-primary-400 dark:text-dark-white font-[600]">
              {currentLanguage === "en"
                ? "Thank you for your enquiry"
                : "شكرًا لتقديم استفسارك"}
            </h2>
            <p className="text-gray-400 dark:text-dark-white body1 ltr:font-family-secondary rtl:font-family-arabic text-center mb-4 px-3">
              {currentLanguage === "en"
                ? `Your enquiry has been successfully submitted to Fujairah Freezone Authority. Our team will review your request and get back to shortly with further details.`
                : "تم تقديم استفسارك بنجاح إلى هيئة المنطقة الحرة في الفجيرة. سيقوم فريقنا بمراجعة طلبك والتواصل معك قريبًا لتزويدك بالمزيد من التفاصيل."}
            </p>

            <BackgroundRevealButton
              isButton={true}
              buttonType="submit"
              buttonOnlickFnc={() => setIsThankYouShown(false)}
              btnLabel={
                currentLanguage === "en"
                  ? "Submit Another Enquiry"
                  : "إرسال استفسار آخر"
              }
              textColor="text-gray-400 dark:text-dark-white body1"
              className="w-fit"
            />
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`w-full max-w-[900px] mx-auto ${extraClass}`}
        >
          {/* Name */}
          <div className="w-full">
            <FontResizerWrapper
              as="label"
              fontClasses={{
                0: "body-0-1",
                1: "body1",
                2: "body-1-1",
              }}
              className="text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[400]"
            >
              {`${translate("Name")} *`}
            </FontResizerWrapper>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder={translate("Your Name")}
                  className="w-full h-[50px] border border-[#005B7E70] dark:border-[#fff] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2 placeholder:dark:text-[#fff]"
                />
              )}
            />
            {errors && errors.name && (
              <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                {errors.name.message?.[currentLanguage] || "N/A"}
              </p>
            )}
          </div>

          {/* Email & Phone */}
          <div className="flex flex-col md:flex-row gap-5 mt-5">
            <div className="w-full">
              <FontResizerWrapper
                as="label"
                fontClasses={{
                  0: "body-0-1",
                  1: "body1",
                  2: "body-1-1",
                }}
                className="text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[400]"
              >
                {`${translate("Email")} *`}
              </FontResizerWrapper>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="email"
                    placeholder={translate("Your Email")}
                    className="w-full h-[50px] border border-[#005B7E70] dark:border-[#fff] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2 placeholder:dark:text-[#fff]"
                  />
                )}
              />
              {errors && errors.email && (
                <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                  {errors.email.message?.[currentLanguage] || "N/A"}
                </p>
              )}
            </div>

            {/* Phone Number */}
            <PhoneNumberInput
              control={control}
              errors={errors}
              label="Phone Number"
              currentLanguage={currentLanguage}
              isDarkMode={true}
            />
          </div>

          {/* Message */}
          <div className="w-full mt-5">
            <FontResizerWrapper
              as="label"
              fontClasses={{
                0: "body-0-1",
                1: "body1",
                2: "body-1-1",
              }}
              className="text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[400]"
            >
              {`${translate("Message")}`}
            </FontResizerWrapper>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={5}
                  type="text"
                  placeholder={translate("Your Message")}
                  className="w-full border border-[#005B7E70] dark:border-[#fff] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 dark:text-dark-white ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2 mb-5 placeholder:dark:text-[#fff]"
                />
              )}
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <BackgroundRevealButton
              isButton={true}
              buttonType="submit"
              btnLabel={
                isSubmitting
                  ? currentLanguage === "en"
                    ? "Loading..."
                    : "جارٍ التحميل..."
                  : currentLanguage === "en"
                    ? "Submit"
                    : "إرسال"
              }
              textColor="text-gray-400 dark:text-dark-white body1"
              className="w-fit"
            />

            {/* <ExternalLinkWrapper
              redirectionLink="https://digital.fujairah.ae/public/portal/?department=/sites/FujairahFreeZone&service=1"
              currentLanguage={currentLanguage}
            >
              <BackgroundRevealButton
                // link="https://digital.fujairah.ae/public/portal/?department=/sites/FujairahFreeZone&service=1"
                // target="_blank"
                isDisabled={true}
                btnLabel={
                  currentLanguage === "en" ? "Register Now" : "سجل الآن"
                }
                textColor="text-gray-400 dark:text-dark-white"
                // customFontSize={{
                //   0: "body-0-2",
                //   1: "body2",
                //   2: "body-2-1",
                // }}
                // btnHeight="h-12"
                // innerBtnContainerWidth="w-12"
                iconSize={18}
              />
            </ExternalLinkWrapper> */}
          </div>
        </form>
      )}
    </>
  );
};

export default PackageEnqiryForm;
